import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

/** Attach event listener to window, auto-removed on unmount */
export function useWindowEvent<K extends keyof WindowEventMap>(
  event: K,
  handler: (e: WindowEventMap[K]) => void,
  options?: AddEventListenerOptions
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    const fn = (e: WindowEventMap[K]) => handlerRef.current(e);
    window.addEventListener(event, fn, options);
    return () => window.removeEventListener(event, fn);
  }, [event]);
}

/** Register event listener on window only when condition is true */
export function useWindowEventConditional<K extends keyof WindowEventMap>(
  event: K,
  condition: () => boolean,
  handler: (e: WindowEventMap[K]) => void
) {
  const handlerRef = useRef(handler);
  const conditionRef = useRef(condition);
  handlerRef.current = handler;
  conditionRef.current = condition;
  useEffect(() => {
    const fn = (e: WindowEventMap[K]) => {
      if (conditionRef.current()) {
        handlerRef.current(e);
      }
    };
    window.addEventListener(event, fn);
    return () => window.removeEventListener(event, fn);
  }, [event]);
}

/** Track size of a DOM node */
export function useDOMNodeSize<T extends Element = HTMLElement>(
  ref: RefObject<T | null>
) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  // Mutating ref.current does not re-render, so sync it into state after commit
  // (needed when the observed node mounts conditionally, e.g. pattern preview).
  const [node, setNode] = useState<T | null>(null);
  useLayoutEffect(() => {
    if (ref.current !== node) {
      setNode(ref.current);
    }
  });

  useEffect(() => {
    if (!node) {
      setSize({ width: 0, height: 0 });
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry) {
        const { width, height } = entry.contentRect;
        setSize({ width, height });
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return size;
}

/** Track window inner size */
export function useWindowInnerSize() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  useEffect(() => {
    const fn = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return size;
}

/**
 * keyboardConsumed returns true if keyboard events should be considered "consumed"
 * by a focused input/textarea/etc.
 */
export function keyboardConsumed(): boolean {
  const active = document.activeElement;
  return (
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement ||
    active instanceof HTMLSelectElement ||
    (active instanceof HTMLElement && active.isContentEditable)
  );
}
